import { useState, useEffect } from "react";
import styled from "styled-components";
import { firebaseApp } from "Components/Firebase";
import {
  CollectionReference,
  DocumentReference,
  collection,
  doc,
  getFirestore,
  query,
} from "firebase/firestore";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import { H1, H3, H2 } from "Components/Text";
import { AthleteCard } from "Components/AthleteCard";
import defaultImg from "Assets/Images/default.jpg";
import { Header } from "Components/Header";
import { Footer } from "Components/Footer";
import { SocialFeed } from "Components/SocialFeed";
import {
  WinnerDocument,
  ConfigDocument,
  YearWinnersDocument,
} from "types/types";
import { useMobileQuery } from "utils/useMobileQuery";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { pop, popDelay } from "Assets/Animate";
import { ButtonPrimary } from "Components/Button";
import { Link } from "react-router-dom";
import logoUIL from "Assets/Images/logo-uil-outlined.png";
import { ReactComponent as DaveCampbellSvg } from "Assets/Svgs/dave-campbells-texas-football.svg";

const MotionH1 = motion(H1);

export const Home = () => {
  const [athleteData, setAthleteData] = useState<WinnerDocument[]>();
  const [athleteYearData, setAthleteYearData] =
    useState<YearWinnersDocument[]>();
  const [displayedWeek, setDisplayedWeek] = useState<number>();
  const [completeSeason, setCompleteSeason] = useState<number | null>();
  const [currentSeason, setCurrentSeason] = useState<number | null>();
  const [loaded, setLoaded] = useState(false);
  const db = getFirestore(firebaseApp);
  const isMobile = useMobileQuery();

  const [site] = useDocument(
    doc(db, "config/site") as DocumentReference<ConfigDocument>,
  );

  const [winnerDocs] = useCollection(
    query(collection(db, "winners") as CollectionReference<WinnerDocument>),
  );
  const [winnerByYearDocs] = useCollection(
    query(
      collection(db, "yearWinners") as CollectionReference<YearWinnersDocument>,
    ),
  );
  const winnerYearDocs = winnerByYearDocs?.docs;

  useEffect(() => {
    const week = site?.data()?.currentWeek;
    const publishedYear = site?.data()?.publishedYear;
    const currentYear = site?.data()?.currentSeason;
    if (week) {
      setDisplayedWeek(week);
    }
    if (currentYear) {
      setCurrentSeason(currentYear);
    }
    if (publishedYear && week) {
      if (week === 12) {
        setCompleteSeason(publishedYear);
      } else {
        setCompleteSeason(null);
      }
    }
  }, [site]);

  useEffect(() => {
    if (winnerByYearDocs) {
      let docData = winnerByYearDocs?.docs?.map((doc) => {
        return { id: doc.id, ...doc?.data() };
      });
      setAthleteYearData(docData);
    }
  }, [winnerByYearDocs]);

  useEffect(() => {
    if (winnerDocs) {
      const docData = winnerDocs?.docs?.map((doc) => {
        return { id: doc.id, ...doc?.data() };
      });
      setAthleteData(docData);
    }
  }, [winnerDocs]);

  const controls = useAnimation();
  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) {
      controls.start("animate");
    }
  }, [controls, inView]);

  const onLoad = () => {
    setLoaded(true);
  };

  return (
    <Main>
      <Header homeScreen={true} />
      <Hero>
        <BackgroundImg
          onLoad={onLoad}
          src="/assets/POTW_Header_Fallback.jpg"
          alt="Ford Player of the Week"
        />
        <UILLogo src={logoUIL} alt="UIL Logo" />
        <DaveCampbellLogo />
      </Hero>
      <CTA>
        <WrapperCTA>
          <MotionH1
            ref={ref}
            animate={controls}
            variants={pop}
            initial="initial"
          >
            Six Divisions.{" "}
          </MotionH1>
          <MotionH1
            ref={ref}
            variants={popDelay}
            initial="initial"
            animate={controls}
          >
            Six Weekly Winners.
          </MotionH1>
        </WrapperCTA>
        <WrapperCTA>
          <H3>
            <span>
              Join The Texas Ford Dealers in recognizing outstanding football
              players in our state.
            </span>{" "}
            Each week The Texas Ford Dealers highlight high school football
            players in classes 2A-6A plus eligible private schools.
          </H3>
          <ButtonPrimary
            as={Link}
            to={`/nominate/`}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Nominate here
          </ButtonPrimary>
        </WrapperCTA>
      </CTA>
      {completeSeason ? (
        <AthleteSection>
          <StyledH1>{completeSeason} Players of the Year</StyledH1>
          <AthleteList>
            {athleteYearData
              ?.sort(
                (a: YearWinnersDocument, b: YearWinnersDocument) =>
                  parseInt(b.athleteClassification || 0) -
                  parseInt(a.athleteClassification || 0),
              )
              .map((data: YearWinnersDocument) => {
                const [year] = (data.id || "")?.split("-");
                return (
                  year === completeSeason.toString().slice(2, 4) && (
                    <AthleteCard
                      src={data.athletePhoto || defaultImg}
                      name={data?.athleteName}
                      grade={data?.athleteGrade}
                      position={data?.athletePosition || ""}
                      school={data?.athleteSchool}
                      category={data?.athleteClassification}
                      details={""}
                    />
                  )
                );
              })}
          </AthleteList>
        </AthleteSection>
      ) : (
        <AthleteSection>
          <StyledH1>Players of the Week {displayedWeek}</StyledH1>
          <AthleteList>
            {athleteData
              ?.sort(
                (a: WinnerDocument, b: WinnerDocument) =>
                  parseInt(b.athleteClassification || 0) -
                  parseInt(a.athleteClassification || 0),
              )
              .map((data: WinnerDocument) => {
                const [year, week] = (data.id || "")?.split("-");
                return (
                  displayedWeek === parseInt(week) &&
                  year === currentSeason?.toString().slice(2, 4) && (
                    <AthleteCard
                      src={data?.athletePhoto || defaultImg}
                      name={data?.athleteName}
                      grade={data?.athleteGrade}
                      position={data?.athletePosition || ""}
                      school={data?.athleteSchool}
                      category={data?.athleteClassification}
                      details={data?.athleteProfile || ""}
                    />
                  )
                );
              })}
          </AthleteList>
        </AthleteSection>
      )}
      <H2>Texas Ford Dealers</H2>
      <SocialFeed />
      <Footer />
    </Main>
  );
};
export const Main = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;
const Hero = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;
const CTA = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  max-width: 1440px;
  background-color: #00142e;
  color: #fff;
  gap: 20px;
  padding: 50px 100px 70px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    padding: 50px 30px;
    gap: 40px;
    text-align: center;
    align-items: center;
    justify-content: center;
  }
`;
const WrapperCTA = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px;
  flex: 1;
  span {
    font-family: "F1Bold";
    display: block;
    padding-bottom: 25px;
  }
  ${({ theme }) => theme.mediaQueries.mobile} {
    align-items: center;
    justify-content: center;
  }
`;
const AthleteSection = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1440px;
  padding: 50px 70px;
  gap: 40px;
  margin-bottom: 80px;
  border-bottom: 1px solid #c2c2c2;
  ${({ theme }) => theme.mediaQueries.mobile} {
    padding: 20px;
  }
`;
export const AthleteList = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  gap: 35px;
  ${({ theme }) => theme.mediaQueries.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${({ theme }) => theme.mediaQueries.mobile} {
    grid-template-columns: 1fr;
  }
`;
const StyledH1 = styled(H1)`
  color: #020c29;
  text-align: left;
  ${({ theme }) => theme.mediaQueries.mobile} {
    text-align: center;
    padding-top: 30px;
  }
`;
const BackgroundImg = styled.img`
  width: 100%;
  object-fit: cover;
  ${({ theme }) => theme.mediaQueries.mobile} {
    height: 80vw;
  }
`;

const UILLogo = styled.img`
  position: absolute;
  width: 72px;
  height: auto;
  left: 28px;
  bottom: 24px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 64px;
  }
`;

const DaveCampbellLogo = styled(DaveCampbellSvg)`
  position: absolute;
  width: 128px;
  height: auto;
  right: 28px;
  bottom: 24px;
  color: #ffffff;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 98px;
  }
`;
